import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';

import AdvanceFileInput from 'components/field/AdvanceFileInput';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import ImageInput from 'components/field/ImageInput';
import IntegerInput from 'components/field/IntegerInput';
import TextArea from 'components/field/TextArea';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Card from 'components/global/Card';
import Typography from 'components/global/Typography';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';

import { openAxios } from 'services/axios';
import { OPEN } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faBox, faCircleCalendar, faDownload, faHashtag, faTruckFast } from '@fortawesome/pro-regular-svg-icons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';

import InfoLabel from './component/InfoLabel';

export const returnRequest1DefaultValues = [
	{
		type: 'color',
		value: '#F5F5F5',
		name: 'pageBackgroundColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'navigationBackgroundColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'iconColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'formBackgroundColor'
	},
	{
		type: 'color',
		value: '#1bc487',
		name: 'saveButtonColor'
	},
	{
		type: 'color',
		value: '#505050',
		name: 'contentTextColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'contentTitleColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'headerTextColor'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showOrderNumber'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showIcon'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnBottom'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnTop'
	}
];

/**
 * @author Burak Ak
 */

const ReturnRequest1 = (props) => {
	const schema = useMemo(
		() =>
			yup.object().shape({
				orderItems: yup.array().of(
					yup.object().shape({
						qtyOrdered: yup.number().nullable(),
						qtyReturnRequested: yup.number().nullable().max(yup.ref('qtyOrdered')).required(),
						qtyTobeReturned: yup.number().nullable()
					})
				),
				returnReason: yup.string().nullable().required(),
				returnReasonOptions: yup.string().nullable().required(),
				returnRequestPhotos: yup
					.array()
					.test(
						'alert.requiredField',
						'alert.requiredField',
						(returnRequestPhotos) =>
							returnRequestPhotos.length !== 1 && returnRequestPhotos[0].photo !== null
					)
					.nullable()
					.max(3)
			}),
		[]
	);

	const {
		returnDeliveryCompanyName,
		orderDate,
		orderNumber,
		returnShippingNumber,
		downloadAWB,
		language,
		propJson,
		returnApprovalStatus,
		orderItems,
		returnReason,
		returnReasonEnumsEn,
		returnReasonEnumsAr,
		returnReasonEnumsTr,
		returnReasonOptions,
		returnRequestPhotos,
		otoOrderID,
		getData
	} = props;
	const { showError, showSuccess } = useAlert();

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			orderItems: null,
			returnReason: null,
			returnReasonOptions: null,
			returnRequestPhotos: [{ photo: null }]
		}
	});

	const {
		handleSubmit,
		watch,
		reset,
		setValue,
		getValues,
		control,
		setError,
		formState: { isSubmitSuccessful, isSubmitting, errors }
	} = formMethods;

	const { fields } = useFieldArray({
		control,
		name: 'orderItems'
	});

	const {
		remove: removeImage,
		fields: imagesField,
		append: appendImage
	} = useFieldArray({
		control,
		name: 'returnRequestPhotos'
	});

	const watchFieldArray = watch('returnRequestPhotos');
	const getFields = useCallback(
		(array) =>
			array.map((field, index) => ({
				...field,
				...watchFieldArray[index]
			})),
		[watchFieldArray]
	);

	useEffect(() => {
		if (imagesField?.length < 3 && !!imagesField[imagesField.length - 1]?.photo) {
			appendImage({ photo: null });
		}
	}, [imagesField]);

	useEffect(() => {
		if (props) {
			reset({
				orderItems:
					orderItems?.map((item) => ({
						...item,
						qtyReturnRequested: item.qtyReturnRequested ?? 0
					})) ?? null,
				returnReason: returnReason ?? null,
				returnReasonOptions: returnReasonOptions ?? null,
				returnRequestPhotos: returnRequestPhotos ?? [{ photo: null }]
			});
		}
	}, [props]);

	const onSubmit = async (data) => {
		const haveItems = data.orderItems.length > 0;
		const haveRequestedItems = data.orderItems.some((item) => item.qtyReturnRequested > 0);

		if (haveItems && !haveRequestedItems) {
			showError({
				message: 'errorMsg.returnRequestMin1'
			});
			throw new Error();
		}

		try {
			const response = await openAxios.post(OPEN.SMS.UPDATE_RETURN_REQUEST_SMS_DETAILS, {
				otoOrderID,
				returnRequestPhotos: data.returnRequestPhotos,
				returnReason: data.returnReason,
				returnReasonOptions: data.returnReasonOptions,
				orderItems: data.orderItems.map((item) => ({
					ID: item.ID,
					qtyReturnRequested: item.qtyReturnRequested
				}))
			});
			if (response.status === 200) {
				showSuccess();
				getData?.();
			} else {
				AppLogger.error(response);
				throw new Error(response);
			}
		} catch (error) {
			if (error?.response?.data?.code === 'error.RR001') {
				showError({
					message: 'error.RR001'
				});
			} else {
				setError('root.serverError', {
					type: error?.response?.data?.code
				});
			}
			AppLogger.error(error);
			showError();
			throw new Error(error);
		}
	};
	const viewMode =
		returnApprovalStatus === 'approved' ||
		returnApprovalStatus === 'rejected' ||
		returnApprovalStatus === 'waitingApproval';

	const columns = useMemo(
		() => [
			...(orderItems?.some((item) => item.productImage)
				? [
						{
							header: 'image',
							accessorKey: 'productImage',
							size: 75,
							renderCell: ({ row: { index } }) => (
								<ImageInput
									viewMode
									name={`orderItems[${index}].productImage`}
									value={watch(`orderItems[${index}].productImage`)}
									setValue={(value) => {
										setValue(`orderItems[${index}].productImage`, value, {
											shouldValidate: true,
											shouldDirty: true
										});
									}}
								/>
							)
						}
					]
				: []),
			{
				header: 'name',
				accessorKey: 'productName',
				size: 125,
				showOnMobile: true
			},
			{
				header: 'orderedQuantity',
				accessorKey: 'qtyOrdered',
				showOnMobile: !viewMode,
				size: 25
			},
			{
				header: 'returnRequestedQuantity',
				accessorKey: 'qtyReturnRequested',
				size: 125,
				showOnMobile: true,
				renderCell: ({ row: { index } }) => (
					<IntegerInput viewMode={viewMode} name={`orderItems[${index}].qtyReturnRequested`} />
				),
				editCell: ({ row: { index } }) => <IntegerInput name={`orderItems[${index}].qtyReturnRequested`} />,
				required: true
			},
			...(returnApprovalStatus === 'approved'
				? [
						{
							header: 'approvedQuantity',
							accessorKey: 'qtyTobeReturned',
							size: 25,
							showOnMobile: viewMode
						}
					]
				: [])
		],
		[viewMode]
	);

	return (
		<FormProvider {...formMethods}>
			<Card background={false} border={0} padding={0}>
				{!returnApprovalStatus && (
					<Typography noWrap variant="p2" fontWeight="500" style={{ color: propJson?.headerTextColor }}>
						returnRequestHeading
					</Typography>
				)}
				{returnApprovalStatus && returnApprovalStatus === 'waitingApproval' && (
					<Alert showIcon variant="warning" content="inform.waitingShipmentReturnRequest" />
				)}
			</Card>
			<br />
			<Card
				background
				border={0}
				shadowed
				rounded
				style={{
					backgroundColor: propJson?.formBackgroundColor
				}}>
				<Typography
					style={{
						color: propJson?.headerTextColor,
						borderTopLeftRadius: '4px',
						borderTopRightRadius: '4px'
					}}
					variant="p1"
					className="pb-5 text-center"
					fontWeight="700">
					{props?.returnApprovalStatus}
				</Typography>
				<div className="row row-cols-1 row-cols-lg-3">
					{propJson?.showOrderNumber && (
						<InfoLabel
							label="orderNumber"
							value={orderNumber}
							icon={propJson?.showIcon && faBox}
							iconSize="x-large"
							labelFontWeight="500"
							labelVariant="p2"
							labelColor={propJson?.contentTitleColor}
							valueColor={propJson?.contentTextColor}
							iconColor={propJson?.iconColor}
						/>
					)}
					<InfoLabel
						label="orderDate"
						value={orderDate}
						icon={propJson?.showIcon && faCircleCalendar}
						iconSize="x-large"
						labelFontWeight="500"
						labelVariant="p2"
						labelColor={propJson?.contentTitleColor}
						valueColor={propJson?.contentTextColor}
						iconColor={propJson?.iconColor}
					/>
					{returnDeliveryCompanyName && downloadAWB && (
						<InfoLabel
							label="returnAWB"
							icon={propJson?.showIcon && faDownload}
							iconSize="x-large"
							labelFontWeight="500"
							labelVariant="p2"
							anchor="downloadAWB"
							anchorLink={downloadAWB}
							labelColor={propJson?.contentTitleColor}
							valueColor={propJson?.contentTextColor}
							iconColor={propJson?.iconColor}
						/>
					)}
				</div>
				<div className="row row-cols-1 row-cols-lg-3">
					{returnDeliveryCompanyName && (
						<InfoLabel
							label="returnDeliveryCompany"
							value={returnDeliveryCompanyName}
							icon={propJson?.showIcon && faTruckFast}
							iconSize="x-large"
							labelFontWeight="500"
							labelVariant="p2"
							labelColor={propJson?.contentTitleColor}
							valueColor={propJson?.contentTextColor}
							iconColor={propJson?.iconColor}
						/>
					)}
					{returnDeliveryCompanyName && returnShippingNumber && (
						<InfoLabel
							label="returnShippingNo"
							value={returnShippingNumber}
							icon={propJson?.showIcon && faHashtag}
							iconSize="x-large"
							labelFontWeight="500"
							labelVariant="p2"
							labelColor={propJson?.contentTitleColor}
							valueColor={propJson?.contentTextColor}
							iconColor={propJson?.iconColor}
						/>
					)}
				</div>
			</Card>
			<Card
				background
				border={0}
				shadowed
				rounded
				className="mt-5"
				style={{
					backgroundColor: propJson?.formBackgroundColor
				}}>
				<Table data={fields} columns={columns} />
			</Card>

			<Card
				background={false}
				border={0}
				padding={0}
				style={{
					padding: '2rem 0'
				}}>
				<Typography variant="p2" fontWeight="500" style={{ color: propJson?.headerTextColor }}>
					returnReason
				</Typography>
			</Card>

			<Card
				padding={1}
				background
				border={0}
				shadowed
				fullHeight
				style={{
					backgroundColor: propJson?.formBackgroundColor
				}}>
				<FormItem label="pleaseSelectYourReturnReason" color={propJson?.headerTextColor} required>
					<EnumSelect
						viewMode={viewMode}
						placeholder="selectOne"
						data={
							language === 'engValue'
								? returnReasonEnumsEn
								: language === 'arabicValue'
									? returnReasonEnumsAr
									: language === 'trValue'
										? returnReasonEnumsTr
										: []
						}
						setValue={(value) => {
							setValue('returnReasonOptions', value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						value={watch('returnReasonOptions')}
						name="returnReasonOptions"
					/>
				</FormItem>

				<FormItem label="pleaseWriteYourReturnReason" color={propJson?.headerTextColor} required>
					<TextArea viewMode={viewMode} placeholder="enterYourNote" name="returnReason" rows="10" />
				</FormItem>

				<FormItem label="returnReasonAttachments" color={propJson?.headerTextColor} required>
					{getFields(imagesField)?.map((item, index) => (
						<AdvanceFileInput
							key={item.id}
							name={`returnRequestPhotos[${index}]`}
							acceptedFileType=".png,.jpg,.jpeg,"
							setValue={(value, err) => {
								setValue(`returnRequestPhotos[${index}].photo`, value, {
									shouldValidate: true,
									shouldDirty: true
								});

								if (value === null || err) removeImage(index);

								const arr = getValues('returnRequestPhotos');

								if ((value === null && err) || (arr.length < 3 && !!arr[arr.length - 1]?.photo)) {
									appendImage({ photo: null });
								}
							}}
							value={item?.photo}
							className="mb-3 mt-3"
							maxFileSize={6}
							fileLimit={1}
							viewMode={viewMode}
							uploadUrl={OPEN.UPLOAD_FILE}
							additionalParams={{ orderID: otoOrderID }}
							errorMessage={errors.returnRequestPhotos?.message}
							disabled={isSubmitSuccessful}
						/>
					))}
				</FormItem>
			</Card>
			<br />
			<div style={{ display: 'flex', justifyContent: 'center' }}>
				{!viewMode && (
					<Button
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						size="large"
						label="submit"
						icon={faCheck}
						style={{ border: propJson?.saveButtonColor, background: propJson?.saveButtonColor }}
						disabled={isSubmitSuccessful}
						loading={isSubmitting}
					/>
				)}
			</div>
		</FormProvider>
	);
};

export default ReturnRequest1;
